import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Navigate, useSearchParams } from 'react-router-dom';
import axios from '../setupAxios';
import { apiUrl } from '../config/api';
import GalleryVirtualGrid from '../components/GalleryVirtualGrid';
import GalleryDeviceList from '../components/GalleryDeviceList';
import { ROLE_SUPER_ADMIN } from '../context/AuthContext';
import {
  CATEGORY_LABELS,
  GALLERY_PANEL,
  VALID_CATEGORIES,
} from '../config/gallery';

/** 图片按文件名排序：asc 升序 / desc 降序 */
const IMAGE_SORT_ASC = 'asc';
const IMAGE_SORT_DESC = 'desc';

function formatImageDateTime(isoString) {
  if (!isoString) return '-';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return isoString;
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function formatCreateTime(isoString) {
  if (!isoString) return '-';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return isoString;
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function formatFileSize(bytes) {
  if (bytes == null) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function canClearDeviceImages() {
  return Number(localStorage.getItem('role') || 0) === ROLE_SUPER_ADMIN;
}

function formatPanelSizeLabel(sizeLabel) {
  if (!sizeLabel) return '';
  return sizeLabel.replace(/\s*×\s*/g, '×');
}

const ImageGallery = () => {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryKey = (category || '').toLowerCase();
  const categoryLabel = CATEGORY_LABELS[categoryKey] || category;
  const panel = GALLERY_PANEL[categoryKey];
  const selectedDevice = searchParams.get('device')?.trim() || null;

  const galleryStyle = panel
    ? {
        '--panel-aspect': `${panel.nativeWidth} / ${panel.nativeHeight}`,
      }
    : undefined;

  const [images, setImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState('');
  /** null=接口原序；asc/desc=按 fileName 字符串排序 */
  const [imageSort, setImageSort] = useState(null);

  const isValidCategory = VALID_CATEGORIES.includes(categoryKey);
  const prevCategoryRef = useRef(categoryKey);

  const openDevice = useCallback(
    (deviceId) => {
      setSearchParams({ device: deviceId }, { replace: false });
    },
    [setSearchParams]
  );

  const backToDeviceList = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const backToDevicelog = useCallback(() => {
    if (!selectedDevice) {
      return;
    }
    const path = `/gallery/${encodeURIComponent(categoryKey)}/log/${encodeURIComponent(selectedDevice)}`;
    window.open(path, '_blank', 'noopener,noreferrer');
  }, [categoryKey, selectedDevice]);
  useEffect(() => {
    if (prevCategoryRef.current !== categoryKey) {
      setSearchParams({}, { replace: true });
      prevCategoryRef.current = categoryKey;
    }
    setImages([]);
    setImageSort(null);
    setError('');
  }, [categoryKey, setSearchParams]);

  useEffect(() => {
    if (!isValidCategory || !selectedDevice) {
      setImages([]);
      setImageSort(null);
      return undefined;
    }

    let cancelled = false;

    const fetchImages = async () => {
      setImagesLoading(true);
      setError('');
      setImageSort(null);
      try {
        const response = await axios.get(
          apiUrl(
            `/api/categories/${encodeURIComponent(categoryKey)}/devices/${encodeURIComponent(selectedDevice)}/images`
          )
        );
        if (!cancelled) {
          setImages(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err) {
        if (!cancelled) {
          if (err.response?.status === 401) {
            setError('登录已过期，请重新登录');
          } else {
            setError('加载图片列表失败');
          }
          console.error('Image list error:', err);
        }
      } finally {
        if (!cancelled) {
          setImagesLoading(false);
        }
      }
    };

    fetchImages();
    return () => {
      cancelled = true;
    };
  }, [categoryKey, selectedDevice, isValidCategory]);

  useEffect(() => {
    if (selectedDevice && !imagesLoading) {
      window.scrollTo(0, 0);
    }
  }, [selectedDevice, imagesLoading, categoryKey]);

  const sortedImages = useMemo(() => {
    if (!imageSort || images.length <= 1) {
      return images;
    }
    const next = [...images];
    next.sort((a, b) => {
      const nameA = a?.fileName || '';
      const nameB = b?.fileName || '';
      const cmp = nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
      return imageSort === IMAGE_SORT_DESC ? -cmp : cmp;
    });
    return next;
  }, [images, imageSort]);

  const toggleImageSort = useCallback(() => {
    setImageSort((prev) =>
      prev === IMAGE_SORT_DESC ? IMAGE_SORT_ASC : IMAGE_SORT_DESC
    );
  }, []);

  const imageSortButtonLabel = imageSort === IMAGE_SORT_ASC ? '图片排序↓' : imageSort === IMAGE_SORT_DESC ? '图片排序↑': '图片排序';
  const handleClearImages = async () => {
    if (!canClearDeviceImages()) {
      window.alert('无权限删除，需要高级管理员账号（users.role = 1）');
      return;
    }

    const confirmed = window.confirm(
      `确定要删除设备「${selectedDevice}」下的所有 PNG 图片和 id.html 吗？\n\n此操作不可恢复。`
    );
    if (!confirmed) {
      return;
    }

    setClearing(true);
    setError('');
    try {
      const response = await axios.delete(
        apiUrl(
          `/api/categories/${encodeURIComponent(categoryKey)}/devices/${encodeURIComponent(selectedDevice)}/images`
        )
      );
      setImages([]);
      const msg = response.data?.message;
      if (msg) {
        window.alert(msg);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError('无权限删除，需要高级管理员账号（role=1）');
      } else if (err.response?.status === 401) {
        setError('登录已过期，请重新登录');
      } else {
        setError('清除图片失败，请稍后重试');
      }
      console.error('Clear images error:', err);
    } finally {
      setClearing(false);
    }
  };

  if (!isValidCategory) {
    return <Navigate to="/gallery/flat" replace />;
  }

  if (!selectedDevice) {
    return (
      <div className={`page-shell gallery-page--devices gallery-page--${categoryKey}`}>
        <p className="gallery-devices-heading">
          {categoryLabel} 设备目录 {panel?.sizeLabel ? `，像素：${formatPanelSizeLabel(panel.sizeLabel)}` : ''}
        </p>
        {error && <div className="gallery-error">{error}</div>}
        <GalleryDeviceList
          categoryKey={categoryKey}
          categoryLabel={categoryLabel}
          onSelectDevice={openDevice}
          formatCreateTime={formatCreateTime}
        />
      </div>
    );
  }

  return (
    <div
      className={`gallery-page gallery-page--${categoryKey} gallery-page--images${panel?.orientation === 'landscape' ? ' gallery-orientation-landscape' : ' gallery-orientation-portrait'}${panel?.displayMode ? ` gallery-${panel.displayMode} gallery-native` : ''}`}
      style={galleryStyle}
    >
      <header className="gallery-header gallery-header--compact">
        <div className="gallery-header-bar">
          <span className="gallery-header-summary">{categoryLabel} {panel?.sizeLabel ? `: ${formatPanelSizeLabel(panel.sizeLabel)}` : ''} 设备编号: {selectedDevice} 共 {sortedImages.length} 张</span>
          <div className="gallery-header-actions">
          <button type="button" className="btn btn-secondary btn-compact" onClick={backToDeviceList} title="返回设备列表" >返回设备列表</button>
          <button type="button" className="btn btn-secondary btn-compact" onClick={backToDevicelog} title="查看日志" >查看日志</button>
          <button type="button" className="btn btn-danger btn-compact" onClick={handleClearImages} title="清除图片" disabled={clearing} >{clearing ? '清除中...' : '清除图片'}</button>
          <button type="button" className="btn btn-secondary btn-compact" onClick={toggleImageSort} disabled={imagesLoading || images.length === 0} title={imageSort === IMAGE_SORT_ASC ? '当前：文件名降序，再点切换为升序'
: imageSort === IMAGE_SORT_DESC ? '当前：文件名升序，再点切换为降序': '按文件名排序（首次点击升序）' } >
            {imageSortButtonLabel}
          </button>
          </div>
        </div>
      </header>

      {error && <div className="gallery-error">{error}</div>}

      {imagesLoading ? (
        <div className="loading">加载图片中...</div>
      ) : sortedImages.length === 0 ? (
        <p className="gallery-empty">该设备暂无图片</p>
      ) : (
        <GalleryVirtualGrid
          images={sortedImages}
          panel={panel}
          formatImageDateTime={formatImageDateTime}
          formatCreateTime={formatCreateTime}
          formatFileSize={formatFileSize}
        />
      )}
    </div>
  );
};

export default ImageGallery;
